import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import { api } from '@services/api';
import FormPageLayout from '../FormPageLayout';
import { LoadingSpinner } from '@components/common';
import './EditPage.scss';

/**
 * Common edit page component for all models
 *
 * @param {Object} props
 * @param {string} props.modelName - Name of the model (e.g., 'categories', 'resources', 'locations')
 * @param {React.ComponentType} props.FormComponent - Form component to render
 * @param {string} props.apiEndpoint - API endpoint for the resource (defaults to `/${modelName}`)
 * @param {string} props.backPath - Path to navigate back to (defaults to `/${modelName}`)
 * @param {string} props.successPath - Path to navigate to on success (defaults to `/${modelName}/{id}` - the view page)
 * @param {Object} props.formProps - Additional props to pass to the form component
 * @param {string} props.className - Additional CSS class
 * @param {React.ReactNode} props.headerActions - Additional header actions
 * @param {Function} props.transformData - Function to transform form data before API call
 * @param {Function} props.onSuccess - Custom success handler (receives updated data)
 * @param {Function} props.onError - Custom error handler (receives error)
 * @param {Function} props.transformFetchedData - Function to transform fetched data before passing to form
 * @param {string} props.idParam - URL parameter name for the ID (defaults to 'id')
 */
const EditPage = ({
    modelName,
    FormComponent,
    apiEndpoint,
    backPath,
    successPath,
    formProps = {},
    className = '',
    headerActions = null,
    transformData = (data) => data,
    onSuccess,
    onError,
    transformFetchedData = (data) => data,
    idParam = 'id'
}) => {
    const params = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get ID from URL params
    const id = params[idParam];

    // Default paths
    const defaultBackPath = backPath || `/${modelName}/${id}`;
    const listPath = successPath || `/${modelName}`;
    const endpoint = apiEndpoint || `/${modelName}`;

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await api.get(`${endpoint}/${id}`);
                const fetchedData = transformFetchedData(response.data);
                setData(fetchedData);
            } catch (err) {
                console.error(`Error fetching ${modelName.slice(0, -1)}:`, err);
                setError(t(`${modelName}.loadError`));
                notify.error(t(`${modelName}.loadError`));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchData();
        }
    }, [id, endpoint, modelName, t]);

    const handleSubmit = async (formData) => {
        try {
            const transformedData = transformData(formData);
            const response = await api.put(`${endpoint}/${id}`, transformedData);

            notify.success(t(`${modelName}.updateSuccess`));

            if (onSuccess) {
                onSuccess(response.data);
            } else {
                // Navigate to the view page of the updated resource
                const viewPath = successPath || `/${modelName}/${id}`;
                navigate(viewPath);
            }

            // Return the response so that form components can access it
            return response;
        } catch (err) {
            console.error(t(`${modelName}.updateError`), err);
            notify.error(t(`${modelName}.updateError`));

            if (onError) {
                onError(err);
            } else {
                throw err;
            }
        }
    };

    const handleCancel = () => {
        // Previous browser history or default back path
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(defaultBackPath);
        }
    };

    // Generate title and subtitle
    const title = t(`${modelName}.editTitle`);
    const subtitle = t(`${modelName}.editSubtitle`);

    // Show loading state
    if (loading) {
        return (
            <FormPageLayout
                title={title}
                subtitle=""
                onBack={handleCancel}
                className={`${modelName.slice(0, -1)}-edit-page ${className}`}
                headerActions={headerActions}
            >
                <div className="loading-container">
                    <LoadingSpinner />
                    <p>{t('common.loading')}</p>
                </div>
            </FormPageLayout>
        );
    }

    // Show error state
    if (error) {
        return (
            <FormPageLayout
                title={title}
                subtitle=""
                onBack={handleCancel}
                className={`${modelName.slice(0, -1)}-edit-page ${className}`}
                headerActions={headerActions}
            >
                <div className="error-container">
                    <p className="error-message">{error}</p>
                </div>
            </FormPageLayout>
        );
    }

    // Show form when data is loaded
    return (
        <FormPageLayout
            title={title}
            subtitle={subtitle}
            onBack={handleCancel}
            className={`${modelName.slice(0, -1)}-edit-page ${className}`}
            headerActions={headerActions}
        >
            <FormComponent
                data={data}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isEditing={true}
                {...formProps}
            />
        </FormPageLayout>
    );
};

export default EditPage;
